import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

/**
 * Admin: Registro de Auditoria
 * Visualiza activity_logs com filtros, busca e paginação.
 */
const AdminAuditLogs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtros
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    status: '',
    search: '',
    startDate: '',
    endDate: '',
    includeAuto: false,
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [expandedRow, setExpandedRow] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Inicial: stats + actions + users
  useEffect(() => {
    const init = async () => {
      try {
        const [s, a, u] = await Promise.all([
          api.get('/admin/audit-logs/stats'),
          api.get('/admin/audit-logs/actions'),
          api.get('/admin/users'),
        ]);
        setStats(s.data);
        setActions(a.data);
        setUsers(u.data);
      } catch (e) {
        console.error('Init audit error:', e);
      }
    };
    init();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (filters.userId) params.userId = filters.userId;
      if (filters.action) params.action = filters.action;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.includeAuto) params.includeAuto = 'true';

      const r = await api.get('/admin/audit-logs', { params });
      setLogs(r.data.items || []);
      setTotalPages(r.data.totalPages || 1);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error('Fetch logs error:', e);
      setError('Erro ao carregar registros de auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Auto-refresh a cada 10s
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => fetchLogs(), 10000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  const updateFilter = (k, v) => {
    setFilters(prev => ({ ...prev, [k]: v }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ userId: '', action: '', status: '', search: '', startDate: '', endDate: '', includeAuto: false });
    setPage(1);
  };

  const formatDate = (s) => {
    if (!s) return '';
    // SQLite armazena no formato 'YYYY-MM-DD HH:MM:SS' (local time já configurado via TZ)
    const d = new Date(s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatRelative = (s) => {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'há instantes';
    if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
    return `há ${Math.floor(diff / 86400)} d`;
  };

  const exportCSV = () => {
    const headers = ['Data/Hora', 'Usuário', 'Email', 'Ação', 'Status', 'IP', 'Detalhes'];
    const rows = logs.map(l => [
      formatDate(l.created_at),
      l.username || '(sem usuário)',
      l.email || '',
      l.label || l.action,
      l.status || '',
      l.ip_address || '',
      l.details ? JSON.stringify(l.details).replace(/"/g, '""') : '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .aal *, .aal *::before, .aal *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .aal { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; -webkit-font-smoothing: antialiased; }

        /* HEADER */
        .aal-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .aal-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; }
        .aal-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .aal-header-back:hover { color: #fff; transform: translateX(-3px); }

        .aal-main { max-width: 1400px; margin: 0 auto; padding: 30px 20px; }

        /* WELCOME */
        .aal-welcome {
          margin-bottom: 24px; padding: 24px 28px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
          border-radius: 18px; border: 1px solid rgba(255,153,0,0.20);
          box-shadow: 0 8px 30px rgba(0,0,0,0.20), 0 0 50px rgba(255,153,0,0.06) inset;
          position: relative; overflow: hidden;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
        }
        .aal-welcome::before {
          content: ''; position: absolute; top: -50%; right: -10%;
          width: 360px; height: 360px;
          background: radial-gradient(circle, rgba(255,153,0,0.18), transparent 70%);
          filter: blur(40px); pointer-events: none;
        }
        .aal-welcome-text { position: relative; z-index: 1; flex: 1; }
        .aal-welcome-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 11px; font-weight: 700; color: #fb923c;
          text-transform: uppercase; letter-spacing: 1.5px;
          padding: 5px 12px; border-radius: 22px;
          background: rgba(255,153,0,0.12); border: 1px solid rgba(255,153,0,0.30);
          margin-bottom: 10px;
        }
        .aal-welcome h2 {
          font-size: 28px; font-weight: 800; color: #fff;
          margin-bottom: 6px; letter-spacing: -0.5px;
          background: linear-gradient(135deg, #fff 0%, #fdba74 100%);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .aal-welcome p { color: rgba(255,255,255,0.70); font-size: 14px; }
        .aal-welcome-icon {
          width: 72px; height: 72px;
          background: linear-gradient(135deg, #f90, #ff6600);
          border-radius: 18px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 10px 28px rgba(255,153,0,0.40), inset 0 1px 0 rgba(255,255,255,0.30);
          flex-shrink: 0; position: relative; z-index: 1;
        }
        .aal-welcome-icon i { font-size: 30px; color: #fff; }

        /* STATS */
        .aal-stats {
          display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin-bottom: 24px;
        }
        .aal-stat-card {
          background: #fff; padding: 20px; border-radius: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
          transition: all 0.3s;
        }
        .aal-stat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.10); }
        .aal-stat-label { font-size: 11px; color: #999; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .aal-stat-value { font-size: 26px; font-weight: 700; color: #f90; }
        .aal-stat-value.err { color: #e53e3e; }
        .aal-stat-value.ok { color: #38a169; }
        .aal-stat-card-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(255,153,0,0.12); color: #f90;
          display: inline-flex; align-items: center; justify-content: center;
          margin-bottom: 8px; font-size: 14px;
        }

        /* FILTERS */
        .aal-filters {
          background: #fff; padding: 20px; border-radius: 14px; margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
        }
        .aal-filters-row {
          display: grid; grid-template-columns: 2fr 1.5fr 1.5fr 1fr 1fr; gap: 12px; margin-bottom: 12px;
        }
        .aal-filters-row.bottom {
          grid-template-columns: 1fr 1fr auto auto auto;
          align-items: center;
        }
        .aal-input, .aal-select {
          padding: 10px 12px; border: 1px solid #e0e0e0; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-size: 13px; color: #333;
          width: 100%; background: #fff; transition: all 0.2s;
        }
        .aal-input:focus, .aal-select:focus {
          outline: none; border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15);
        }
        .aal-input-search {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: 10px center; background-size: 16px;
          padding-left: 36px;
        }
        .aal-toggle-auto {
          display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
          padding: 8px 12px; border-radius: 10px; font-size: 13px; color: #555;
          border: 1px solid transparent; transition: all 0.2s;
        }
        .aal-toggle-auto:hover { background: #fafafa; }
        .aal-toggle-auto input { cursor: pointer; }
        .aal-btn {
          padding: 10px 16px; border: none; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 13px;
          cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
          transition: all 0.2s;
        }
        .aal-btn-clear { background: #f0f0f0; color: #555; }
        .aal-btn-clear:hover { background: #e0e0e0; }
        .aal-btn-csv { background: linear-gradient(135deg, #38a169, #2f855a); color: #fff; }
        .aal-btn-csv:hover { box-shadow: 0 4px 14px rgba(56,161,105,0.30); }
        .aal-btn-refresh { background: linear-gradient(135deg, #f90, #ff6600); color: #fff; }
        .aal-btn-refresh:hover { box-shadow: 0 4px 14px rgba(255,153,0,0.30); }
        .aal-btn-refresh.active {
          background: linear-gradient(135deg, #38a169, #2f855a);
          box-shadow: 0 0 0 4px rgba(56,161,105,0.15), 0 4px 14px rgba(56,161,105,0.30);
        }

        /* TABLE */
        .aal-table-wrap {
          background: #fff; border-radius: 14px; overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
        }
        .aal-table-head {
          padding: 16px 20px; border-bottom: 1px solid #eee;
          display: flex; justify-content: space-between; align-items: center; gap: 12px;
        }
        .aal-table-head h3 { font-size: 16px; color: #333; }
        .aal-table-head .count { color: #999; font-size: 13px; font-weight: 500; }

        .aal-table-scroll { overflow-x: auto; }
        .aal-table { width: 100%; border-collapse: collapse; }
        .aal-table th {
          text-align: left; padding: 12px 16px; font-size: 11px;
          font-weight: 700; color: #999; text-transform: uppercase;
          letter-spacing: 0.5px; background: #fafafa; border-bottom: 1px solid #eee;
          position: sticky; top: 0;
        }
        .aal-table td {
          padding: 14px 16px; font-size: 13px; color: #444;
          border-bottom: 1px solid #f0f0f0; vertical-align: middle;
        }
        .aal-table tr { transition: background 0.15s; cursor: pointer; }
        .aal-table tr:hover { background: #fffaf2; }
        .aal-table tr.expanded { background: #fffaf2; }
        .aal-table tr.error-row { background: rgba(229,62,62,0.04); }
        .aal-table tr.error-row:hover { background: rgba(229,62,62,0.08); }

        .aal-time { white-space: nowrap; }
        .aal-time-abs { font-weight: 600; color: #333; font-size: 13px; }
        .aal-time-rel { font-size: 11px; color: #999; }

        .aal-user-cell { display: flex; align-items: center; gap: 10px; }
        .aal-user-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: linear-gradient(135deg, #f90, #ff6600);
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-weight: 700; font-size: 12px; flex-shrink: 0;
        }
        .aal-user-info { min-width: 0; }
        .aal-user-name { font-weight: 600; color: #333; font-size: 13px; }
        .aal-user-email { font-size: 11px; color: #999; }
        .aal-user-anon { color: #999; font-style: italic; }

        .aal-action {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 10px; border-radius: 8px; font-size: 12px; font-weight: 600;
        }
        .aal-action.orange { background: rgba(255,153,0,0.12); color: #d97706; }
        .aal-action.green  { background: rgba(56,161,105,0.12); color: #2f855a; }
        .aal-action.red    { background: rgba(229,62,62,0.12); color: #c53030; }
        .aal-action.blue   { background: rgba(59,130,246,0.12); color: #2563eb; }
        .aal-action.gray   { background: rgba(148,163,184,0.15); color: #64748b; }

        .aal-status {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 6px;
        }
        .aal-status.success { background: rgba(56,161,105,0.15); color: #2f855a; }
        .aal-status.error   { background: rgba(229,62,62,0.15); color: #c53030; }

        .aal-ip { font-family: 'SF Mono', Consolas, monospace; font-size: 12px; color: #777; }

        /* EXPANDED DETAILS */
        .aal-details-row td {
          padding: 0 !important; background: #fafafa;
        }
        .aal-details {
          padding: 16px 60px; font-size: 12px; color: #555;
          border-top: 1px dashed #e0e0e0;
        }
        .aal-details-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 8px;
        }
        .aal-details-block { background: #fff; padding: 12px 14px; border-radius: 8px; border: 1px solid #eee; }
        .aal-details-block h4 {
          font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .aal-details-block pre {
          font-family: 'SF Mono', Consolas, monospace; font-size: 11px; color: #333;
          white-space: pre-wrap; word-break: break-all; line-height: 1.5;
        }
        .aal-details-block code { font-family: inherit; }

        /* PAGINATION */
        .aal-pagination {
          padding: 16px 20px; display: flex; justify-content: space-between; align-items: center;
          border-top: 1px solid #eee; gap: 12px; flex-wrap: wrap;
        }
        .aal-pagination-info { font-size: 13px; color: #777; }
        .aal-pagination-controls { display: flex; gap: 6px; align-items: center; }
        .aal-page-btn {
          width: 36px; height: 36px; border-radius: 8px; border: 1px solid #e0e0e0;
          background: #fff; cursor: pointer; font-weight: 600; font-size: 13px; color: #555;
          transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center;
        }
        .aal-page-btn:hover:not(:disabled) { border-color: #f90; color: #f90; }
        .aal-page-btn.active { background: #f90; color: #fff; border-color: #f90; }
        .aal-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .aal-empty, .aal-loading-row {
          padding: 60px 20px; text-align: center; color: #999;
        }
        .aal-empty i { font-size: 48px; margin-bottom: 12px; color: #ddd; display: block; }
        .aal-empty p { font-size: 14px; }
        .aal-spinner {
          width: 28px; height: 28px; border: 3px solid rgba(255,153,0,0.2);
          border-top-color: #f90; border-radius: 50%; animation: aal-spin 0.8s linear infinite;
          margin: 0 auto 12px;
        }
        @keyframes aal-spin { to { transform: rotate(360deg); } }

        .aal-error-banner {
          background: rgba(229,62,62,0.10); color: #c53030;
          padding: 12px 16px; border-radius: 10px; margin-bottom: 16px;
          font-weight: 500; border: 1px solid rgba(229,62,62,0.20);
        }

        @media (max-width: 1280px) {
          .aal-stats { grid-template-columns: repeat(2, 1fr); }
          .aal-filters-row { grid-template-columns: 1fr 1fr; }
          .aal-filters-row.bottom { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 640px) {
          .aal-header { padding: 14px 18px; }
          .aal-main { padding: 18px 12px; }
          .aal-stats { grid-template-columns: 1fr; }
          .aal-filters-row { grid-template-columns: 1fr; }
          .aal-table th, .aal-table td { padding: 10px 12px; font-size: 12px; }
          .aal-details { padding: 14px 16px; }
          .aal-details-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="aal">
        <header className="aal-header">
          <h1 className="aal-header-title">Registro de Auditoria</h1>
          {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
        </header>

        <main className="aal-main">
          {/* Welcome */}
          <div className="aal-welcome">
            <div className="aal-welcome-text">
              <div className="aal-welcome-eyebrow">
                <i className="fas fa-shield-alt"></i> Monitoramento ativo
              </div>
              <h2>Registro de Auditoria</h2>
              <p>Acompanhe todas as ações realizadas pelos usuários da plataforma — login, geração de códigos, compras, edições e mais.</p>
            </div>
            <div className="aal-welcome-icon">
              <i className="fas fa-clipboard-list"></i>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="aal-stats">
              <div className="aal-stat-card">
                <div className="aal-stat-card-icon"><i className="fas fa-clock"></i></div>
                <div className="aal-stat-label">Eventos Hoje</div>
                <div className="aal-stat-value">{stats.totalToday}</div>
              </div>
              <div className="aal-stat-card">
                <div className="aal-stat-card-icon"><i className="fas fa-calendar-week"></i></div>
                <div className="aal-stat-label">Últimos 7 dias</div>
                <div className="aal-stat-value">{stats.totalWeek}</div>
              </div>
              <div className="aal-stat-card">
                <div className="aal-stat-card-icon"><i className="fas fa-calendar-alt"></i></div>
                <div className="aal-stat-label">Últimos 30 dias</div>
                <div className="aal-stat-value">{stats.totalMonth}</div>
              </div>
              <div className="aal-stat-card">
                <div className="aal-stat-card-icon"><i className="fas fa-database"></i></div>
                <div className="aal-stat-label">Total geral</div>
                <div className="aal-stat-value ok">{stats.totalAll}</div>
              </div>
              <div className="aal-stat-card">
                <div className="aal-stat-card-icon" style={{ background: 'rgba(229,62,62,0.12)', color: '#e53e3e' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className="aal-stat-label">Erros (24h)</div>
                <div className={`aal-stat-value ${stats.errors24h > 0 ? 'err' : 'ok'}`}>{stats.errors24h}</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="aal-filters">
            <div className="aal-filters-row">
              <input
                type="text"
                className="aal-input aal-input-search"
                placeholder="Buscar por usuário, email, ação ou detalhes..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
              />
              <select
                className="aal-select"
                value={filters.userId}
                onChange={(e) => updateFilter('userId', e.target.value)}
              >
                <option value="">Todos os usuários</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.email})
                  </option>
                ))}
              </select>
              <select
                className="aal-select"
                value={filters.action}
                onChange={(e) => updateFilter('action', e.target.value)}
              >
                <option value="">Todas as ações</option>
                {actions.map(a => (
                  <option key={a.action} value={a.action}>
                    {a.label} ({a.total})
                  </option>
                ))}
              </select>
              <select
                className="aal-select"
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
              >
                <option value="">Todos status</option>
                <option value="success">Sucesso</option>
                <option value="error">Erro</option>
              </select>
              <input
                type="date"
                className="aal-input"
                value={filters.startDate}
                onChange={(e) => updateFilter('startDate', e.target.value)}
                title="Data inicial"
              />
            </div>
            <div className="aal-filters-row bottom">
              <input
                type="date"
                className="aal-input"
                value={filters.endDate}
                onChange={(e) => updateFilter('endDate', e.target.value)}
                title="Data final"
              />
              <label className="aal-toggle-auto">
                <input
                  type="checkbox"
                  checked={filters.includeAuto}
                  onChange={(e) => updateFilter('includeAuto', e.target.checked)}
                />
                Incluir requests GET automáticos
              </label>
              <button className="aal-btn aal-btn-clear" onClick={clearFilters}>
                <i className="fas fa-times"></i> Limpar
              </button>
              <button className="aal-btn aal-btn-csv" onClick={exportCSV} disabled={!logs.length}>
                <i className="fas fa-download"></i> Exportar CSV
              </button>
              <button
                className={`aal-btn aal-btn-refresh ${autoRefresh ? 'active' : ''}`}
                onClick={() => setAutoRefresh(a => !a)}
                title="Atualizar automaticamente a cada 10s"
              >
                <i className={`fas ${autoRefresh ? 'fa-pause' : 'fa-sync-alt'}`}></i>
                {autoRefresh ? ' Auto ON' : ' Atualizar'}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <div className="aal-error-banner"><i className="fas fa-exclamation-circle"></i> {error}</div>}

          {/* Table */}
          <div className="aal-table-wrap">
            <div className="aal-table-head">
              <h3>Eventos registrados</h3>
              <span className="count">{loading ? 'Carregando...' : `${total} registro${total !== 1 ? 's' : ''}`}</span>
            </div>

            {loading ? (
              <div className="aal-loading-row">
                <div className="aal-spinner"></div>
                <p>Carregando registros...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="aal-empty">
                <i className="fas fa-folder-open"></i>
                <p>Nenhum registro encontrado para esses filtros.</p>
              </div>
            ) : (
              <div className="aal-table-scroll">
              <table className="aal-table">
                <thead>
                  <tr>
                    <th>Data / Hora</th>
                    <th>Usuário</th>
                    <th>Ação</th>
                    <th>Status</th>
                    <th>IP</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`${log.status === 'error' ? 'error-row' : ''} ${expandedRow === log.id ? 'expanded' : ''}`}
                        onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                      >
                        <td className="aal-time">
                          <div className="aal-time-abs">{formatDate(log.created_at)}</div>
                          <div className="aal-time-rel">{formatRelative(log.created_at)}</div>
                        </td>
                        <td>
                          {log.user_id ? (
                            <div className="aal-user-cell">
                              <div className="aal-user-avatar">
                                {(log.username || '?').slice(0, 1).toUpperCase()}
                              </div>
                              <div className="aal-user-info">
                                <div className="aal-user-name">
                                  {log.username || `ID ${log.user_id}`}
                                  {log.is_admin === 1 && <i className="fas fa-shield-alt" style={{ marginLeft: 6, color: '#f90', fontSize: 11 }} title="Admin"></i>}
                                </div>
                                <div className="aal-user-email">{log.email || ''}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="aal-user-anon">— anônimo —</span>
                          )}
                        </td>
                        <td>
                          <span className={`aal-action ${log.color || 'gray'}`}>
                            <i className={`fas ${log.icon || 'fa-circle-info'}`}></i>
                            {log.label || log.action}
                          </span>
                        </td>
                        <td>
                          <span className={`aal-status ${log.status === 'error' ? 'error' : 'success'}`}>
                            <i className={`fas ${log.status === 'error' ? 'fa-times-circle' : 'fa-check-circle'}`}></i>
                            {log.status === 'error' ? 'Erro' : 'OK'}
                          </span>
                        </td>
                        <td><span className="aal-ip">{log.ip_address || '—'}</span></td>
                        <td>
                          <i className={`fas fa-chevron-${expandedRow === log.id ? 'up' : 'down'}`} style={{ color: '#bbb', fontSize: 12 }}></i>
                        </td>
                      </tr>
                      {expandedRow === log.id && (
                        <tr className="aal-details-row">
                          <td colSpan={6}>
                            <div className="aal-details">
                              <div className="aal-details-grid">
                                <div className="aal-details-block">
                                  <h4>Ação técnica</h4>
                                  <pre><code>{log.action}</code></pre>
                                </div>
                                <div className="aal-details-block">
                                  <h4>User-Agent</h4>
                                  <pre><code>{log.user_agent || '—'}</code></pre>
                                </div>
                                {log.details && (
                                  <div className="aal-details-block" style={{ gridColumn: '1 / -1' }}>
                                    <h4>Detalhes</h4>
                                    <pre><code>{typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}</code></pre>
                                  </div>
                                )}
                                {log.error_message && (
                                  <div className="aal-details-block" style={{ gridColumn: '1 / -1', borderColor: 'rgba(229,62,62,0.30)' }}>
                                    <h4 style={{ color: '#c53030' }}>Mensagem de erro</h4>
                                    <pre><code>{log.error_message}</code></pre>
                                  </div>
                                )}
                                {log.entity_type && (
                                  <div className="aal-details-block">
                                    <h4>Entidade</h4>
                                    <pre><code>{log.entity_type} #{log.entity_id || '—'}</code></pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="aal-pagination">
                <div className="aal-pagination-info">
                  Página {page} de {totalPages} — {total} registro{total !== 1 ? 's' : ''}
                </div>
                <div className="aal-pagination-controls">
                  <button className="aal-page-btn" disabled={page === 1} onClick={() => setPage(1)}>
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  <button className="aal-page-btn" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                    <i className="fas fa-angle-left"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                    let p;
                    if (totalPages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                    else p = page - 2 + i;
                    return (
                      <button key={p} className={`aal-page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>
                        {p}
                      </button>
                    );
                  })}
                  <button className="aal-page-btn" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                    <i className="fas fa-angle-right"></i>
                  </button>
                  <button className="aal-page-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminAuditLogs;
